"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth-client";
import { useApi } from "@/lib/fetch-client";
import { getLoginRedirectPath, isAuthPathname, isSessionInvalidError } from "@llmgateway/shared/auth-session";
export interface UserUpdateData {
  name?: string;
  email?: string;
}
export interface PasswordUpdateData {
  currentPassword: string;
  newPassword: string;
}
export interface UseUserOptions {
  redirectTo?: string;
  redirectWhen?: "authenticated" | "unauthenticated";
}
let sessionInvalidationInProgress = false;
let sessionInvalidationHandled = false;
export function useUser(options?: UseUserOptions) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const api = useApi();
  const pathname = usePathname();
  const {
    signOut
  } = useAuth();
  const {
    data,
    isLoading,
    error
  } = api.useQuery("get", "/user/me", {}, {
    retry: 0,
    staleTime: 5 * 60 * 1000,
    // 5 minutes
    refetchOnWindowFocus: false
  });
  useEffect(() => {
    if (data?.user) {
      sessionInvalidationHandled = false;
    }
  }, [data?.user]);
  useEffect(() => {
    if (!error || !isSessionInvalidError(error)) {
      return;
    }
    if (sessionInvalidationInProgress || sessionInvalidationHandled) {
      return;
    }
    sessionInvalidationInProgress = true;
    void signOut().catch(() => undefined).finally(() => {
      queryClient.clear();
      sessionInvalidationHandled = true;
      sessionInvalidationInProgress = false;
      if (options?.redirectWhen === "unauthenticated" && !isAuthPathname(pathname)) {
        router.replace(getLoginRedirectPath(pathname));
      }
    });
  }, [error, options?.redirectWhen, pathname, queryClient, router, signOut]);

  // Check for onboarding completion for all authenticated users
  useEffect(() => {
    if (!data?.user || isLoading) {
      return;
    }
    const currentPath = pathname;
    const isAuthPage = ["/login", "/signup"].includes(currentPath);
    const isLandingPage = currentPath === "/";

    // Don't redirect if already on auth pages
    if (isAuthPage || isLandingPage) {
      // No action needed for auth/landing pages
    }
  }, [data?.user, isLoading, router, pathname]);

  // Handle existing redirect logic
  useEffect(() => {
    if (!options?.redirectTo || !options?.redirectWhen) {
      return;
    }
    const {
      redirectTo,
      redirectWhen
    } = options;
    const hasUser = !!data?.user;
    if (error && isSessionInvalidError(error)) {
      return;
    }
    if (redirectWhen === "authenticated" && hasUser) {
      router.push(redirectTo);
    } else if (redirectWhen === "unauthenticated" && !isLoading && (!hasUser || error)) {
      router.push(redirectTo);
    }
  }, [data?.user, isLoading, error, router, options?.redirectTo, options?.redirectWhen, options]);
  return {
    user: data?.user ?? null,
    isLoading,
    error,
    data
  };
}
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const api = useApi();
  return api.useMutation("patch", "/user/me", {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["user"]
      });
      void queryClient.invalidateQueries({
        queryKey: ["session"]
      });
    }
  });
}
export function useUpdatePassword() {
  const api = useApi();
  return api.useMutation("put", "/user/password");
}
export function useDeleteAccount() {
  const api = useApi();
  return api.useMutation("delete", "/user/me");
}